import RedeemIcon from '@mui/icons-material/Redeem';
import {
  Button,
  Card,
  CircularProgress,
  darken,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { BottomSheet } from '@ui';
import { formatDuration, subMonths } from 'date-fns';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import usePastBaskets from '../store/cycles/usePastBasket';
import useActivateFreeTrial from '../store/freeTrial/useActivateFreeTrial';
import useFreeTrialStatus from '../store/freeTrial/useFreeTrialStatus';
import { useBothUserPlans } from '../store/subscribe/usePlans';
import useBrokerConfig from '../store/useBrokerConfig';
import { useMixedPanel } from '../store/useMixPanel';

import { useTheme } from '@mui/material';
import { Lobster } from 'next/font/google';

const LobsterFont = Lobster({ subsets: ['latin'], weight: '400' });

type FreeTrialBannerProps = {
  onFreeTrialActivated?: () => void;
};

const FreeTrialBanner = forwardRef(
  ({ onFreeTrialActivated }: FreeTrialBannerProps, ref) => {
    const {
      data: freeTrialStatus,
      isLoading,
      refetch: refetchFreeTrialStatus,
    } = useFreeTrialStatus();
    const {
      mutateAsync: activateFreeTrial,
      isPending: isActivatingFreeTrial,
      isSuccess: isActivatedFreeTrial,
      isError: isErrorActivatingFreeTrial,
      error: activateFreeTrialError,
    } = useActivateFreeTrial();

    const onFreeTrialBannerClicked = () => {
      if (freeTrialStatus?.freeTrialStatus === 'not_active') {
        trackEvent('CLICK: Show free trial info');
        setBottomSheetOpen(true);
      }
    };
    useImperativeHandle(ref, () => ({
      onFreeTrialBannerClicked,
    }));

    const { data: brokerConfig } = useBrokerConfig();

    const { long, short, refetch: refetchPlans } = useBothUserPlans();
    const [bottomSheetOpen, setBottomSheetOpen] = useState(false);

    const { dataSource: pastBaskets } = usePastBaskets(
      subMonths(new Date(), 3).getTime()
    );

    const getFreeTrialDurationText = useMemo(() => {
      if (!freeTrialStatus?.freeTrialDuration) return '';
      return formatDuration(freeTrialStatus.freeTrialDuration);
    }, [freeTrialStatus]);

    const getFreeTrialDurationLeftText = useMemo(() => {
      if (!freeTrialStatus?.freeTrialDuration) return '';
      if (freeTrialStatus.pendingDays === 0) return 'today';
      if (freeTrialStatus.pendingDays === 1) return 'tomorrow';
      return `in ${formatDuration({ days: freeTrialStatus.pendingDays })}`;
    }, [freeTrialStatus]);

    useEffect(() => {
      if (isErrorActivatingFreeTrial) {
        setBottomSheetOpen(false);
        toast.error(activateFreeTrialError.message);
      }

      if (isActivatedFreeTrial) {
        onFreeTrialActivated?.call(this);
        refetchPlans();
        refetchFreeTrialStatus().then(() => {
          setBottomSheetOpen(false);
          toast.success('Free trial activated successfully');
        });
      }
    }, [
      isActivatedFreeTrial,
      isErrorActivatingFreeTrial,
      activateFreeTrialError?.message,
    ]);

    const { trackEvent } = useMixedPanel();
    const { palette } = useTheme();

    const statusCard = useMemo(() => {
      return (
        <Card
          onClick={() => onFreeTrialBannerClicked()}
          sx={{
            cursor: 'pointer',
            minHeight: '120px',
            position: 'relative',
          }}
        >
          <Stack
            gap={2}
            direction={'row'}
            sx={{
              minHeight: '120px',
              alignItems: 'center',
              zIndex: 3,
              p: 2,
              color: 'white',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction={'column'} gap={1}>
              <Typography
                variant="h6"
                sx={{
                  textWrap: 'balance',
                  flex: 1,
                }}
              >
                {freeTrialStatus?.freeTrialStatus === 'active' &&
                  `Free trial expires  ${getFreeTrialDurationLeftText}`}
                {freeTrialStatus?.freeTrialStatus === 'not_active' &&
                  `Get ${getFreeTrialDurationText} of Intraday recommendations for free`}
              </Typography>
              {freeTrialStatus?.freeTrialStatus === 'not_active' && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    width: '120px',
                    ':hover': {
                      backgroundColor: 'white',
                      color: 'primary.main',
                    },
                  }}
                >
                  Claim Offer
                </Button>
              )}
            </Stack>
            <RedeemIcon sx={{ fontSize: 64 }} />
          </Stack>
          <Stack
            sx={{
              minHeight: '120px',
              height: '100%',
              backgroundColor: '#e5e5f7',
              background: `linear-gradient(160deg,  ${darken(
                palette.primary.main,
                0.8
              )},${palette.primary.main});`,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              zIndex: -1,
            }}
          />
        </Card>
      );
    }, [getFreeTrialDurationText, freeTrialStatus, palette]);

    const bottomSheet = useMemo(() => {
      return (
        <BottomSheet
          isOpen={bottomSheetOpen}
          showCloseButton={true}
          onClose={() => {
            trackEvent('CLICK: Close free trial info');
            setBottomSheetOpen(false);
          }}
          content={
            <Stack
              direction={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              p={2}
            >
              <RedeemIcon sx={{ fontSize: 96, color: 'primary.main' }} />
              <Typography
                sx={{
                  fontFamily: LobsterFont.style.fontFamily,
                  textWrap: 'balance',
                  color: 'primary.main',
                }}
                variant="h3"
                textAlign={'center'}
              >
                Congratulations!
              </Typography>
              <Typography
                variant="h6"
                textAlign={'center'}
                sx={{ textWrap: 'balance' }}
                mt={2}
              >
                You are eligible for {getFreeTrialDurationText} of Intraday
                recommendations for free
              </Typography>

              <Typography
                mt={2}
                sx={{ textWrap: 'balance' }}
                textAlign="center"
              >
                {`By signing up for the free trial, you'll enjoy ${getFreeTrialDurationText} of complimentary access to both long
              and short intraday baskets. After the trial period ends, you'll
              need to subscribe to access the recommendations.`}
              </Typography>
            </Stack>
          }
          footer={
            <Stack direction={'row'} m={2}>
              <Button
                variant="contained"
                fullWidth
                disabled={isActivatingFreeTrial}
                onClick={() => {
                  trackEvent('CLICK: Activate free trial');
                  activateFreeTrial();
                }}
              >
                {isActivatingFreeTrial ? (
                  <CircularProgress size={24} />
                ) : (
                  'Start Free Trial'
                )}
              </Button>
            </Stack>
          }
        />
      );
    }, [freeTrialStatus, bottomSheetOpen, isActivatingFreeTrial]);

    if (isLoading) {
      return <Skeleton />;
    }
    if (freeTrialStatus?.freeTrialStatus === 'active') {
      return <>{statusCard}</>;
    }

    /*
    Show activate free trial button if free trial is not active and there are no past baskets
    in the last 3 months and free trial is  enabled in broker config
  */
    if (
      freeTrialStatus?.freeTrialStatus === 'not_active' &&
      brokerConfig?.broker_config.free_trial &&
      pastBaskets.length === 0 &&
      !(long?.skipPayment || short?.skipPayment) && !isActivatedFreeTrial
    ) {
      return (
        <>
          {bottomSheet}
          {statusCard}
        </>
      );
    }
    return <></>;
  }
);
FreeTrialBanner.displayName = 'FreeTrialBanner';
export default FreeTrialBanner;
